'use client';

import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { FacebookIcon } from '@app/app/_icons/FacebookIcon';
import { InstagramIcon } from '@app/app/_icons/InstagramIcon';
import { LinkedinIcon } from '@app/app/_icons/LinkedinIcon';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

interface Props {
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    className?: string;
}

export function EsalesTransportFooter({ stakeholderLogoSrc, vigLogoSrc, className }: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const actualInformation = useMemo(() => {
        return [
            { label: 'eshop.transport.footer.insuranceRules', url: parameters.eshopFooterUrls.insuranceRules },
            { label: 'eshop.transport.footer.privacyPolicy', url: parameters.eshopFooterUrls.privacyPolicy },
            {
                label: 'eshop.transport.footer.cookiesPolicy',
                url: parameters.eshopFooterUrls.cookiesPolicy,
            },
            {
                label: 'eshop.transport.footer.accessibility',
                url: parameters.eshopFooterUrls.accessibility,
            },
        ];
    }, [parameters.eshopFooterUrls]);

    const aboutUsLinks = useMemo(() => {
        return [{ label: 'eshop.transport.footer.contacts', url: parameters.eshopFooterUrls.contacts }];
    }, [parameters.eshopFooterUrls]);

    const socialLinks = useMemo(() => {
        return [
            {
                label: 'eshop.transport.footer.facebook',
                url: parameters.eshopFooterUrls.facebook,
                icon: <FacebookIcon className="size-6" />,
            },
            {
                label: 'eshop.transport.footer.instagram',
                url: parameters.eshopFooterUrls.instagram,
                icon: <InstagramIcon className="size-6" />,
            },
            {
                label: 'eshop.transport.footer.linkedin',
                url: parameters.eshopFooterUrls.linkedin,
                icon: <LinkedinIcon className="size-6" />,
            },
        ];
    }, [parameters.eshopFooterUrls]);

    return (
        <footer className={twMerge('relative mt-auto w-screen bg-white px-6 py-12 lg:bottom-0', className)}>
            <div className="mx-auto grid max-w-[1350px] grid-cols-1 gap-8 md:grid-cols-3">
                <div className="flex flex-col text-start">
                    <Paragraph isBold size="medium" className="mb-6">
                        {t('eshop.transport.footer.actualInformation')}
                    </Paragraph>
                    <ul className="flex flex-col gap-3">
                        {actualInformation.map(item => (
                            <li key={item.label}>
                                <Link href={item.url} target="_blank" rel="noopener noreferrer">
                                    <Paragraph className="transition-all duration-200 hover:text-primary-500">
                                        {t(item.label)}
                                    </Paragraph>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="flex flex-col text-start">
                    <Paragraph isBold size="medium" className="mb-6">
                        {t('eshop.transport.footer.aboutUs')}
                    </Paragraph>
                    <ul className="flex flex-col gap-3">
                        {aboutUsLinks.map(item => (
                            <li key={item.label}>
                                <Link href={item.url} target="_blank" rel="noopener noreferrer">
                                    <Paragraph className="transition-all duration-200 hover:text-primary-500">
                                        {t(item.label)}
                                    </Paragraph>
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-6">
                        <Paragraph isBold className="mb-3">
                            {t('eshop.transport.footer.needHelp')}
                        </Paragraph>
                        <div className="flex flex-col gap-2">
                            <Paragraph>{t('eshop.transport.footer.phoneNumber')}</Paragraph>
                            <Link href="mailto:info@compensa.lt">
                                <Paragraph className="transition-all duration-200 hover:text-primary-500">
                                    {t('eshop.transport.footer.email')}
                                </Paragraph>
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col text-start">
                    <Paragraph isBold size="medium" className="mb-6">
                        {t('eshop.transport.footer.followUs')}
                    </Paragraph>
                    <ul className="flex flex-col gap-3">
                        {socialLinks.map(item => (
                            <li key={item.label}>
                                <Link
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 transition-all duration-200 hover:text-primary-500">
                                    <div className="rounded-full border p-1">{item.icon}</div>
                                    <span>{t(item.label)}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="mx-auto flex w-full max-w-[1350px] flex-col gap-12 pt-12 md:flex-row md:items-center">
                <div className="flex flex-row gap-4">
                    <Image
                        unoptimized
                        width={64}
                        height={32}
                        className="object-contain"
                        src={vigLogoSrc}
                        alt={t('eshop.transport.footer.vigLogoAlt')}
                    />
                    <Paragraph>{t('eshop.transport.footer.vigLogoText')}</Paragraph>
                </div>
                <div className="flex flex-row gap-4">
                    <Image
                        unoptimized
                        width={124}
                        height={32}
                        className="object-contain"
                        src={stakeholderLogoSrc}
                        alt={t('eshop.transport.footer.stakeholderLogoAlt')}
                    />
                    <Paragraph>{t('eshop.transport.footer.stakeholderLogoText')}</Paragraph>
                </div>
            </div>
        </footer>
    );
}
